import { LitElement, html } from 'lit-element';
import { ifDefined } from 'lit-html/directives/if-defined.js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import {
    KEY_ENTER,
    KEY_ESCAPE,
    KEY_ESC,
    KEY_ARROW_UP,
    KEY_UP,
    KEY_ARROW_DOWN,
    KEY_DOWN,
    KEY_SPACE,
    KEY_SPACE_BAR,
} from '../../core/scripts/constants/keyboard';
import { isEventRelatedTargetWithinCurrentTarget } from '../../core/scripts/util/react';

const ID_REGEX = / |\//g;

/**
 * Container component for custom multi-select dropdowns
 * @class MultiSelect
 * @extends LitElement
 */
class MultiSelect extends LitElement {
    static get properties() {
        return {
            // Public
            identifier: { type: String },
            label: { type: String },
            placeholder: { type: String },
            displayOnlyPlaceholder: { type: Boolean },
            items: { type: Array },
            multiple: { type: Boolean },
            selected: { type: Array },
            autosuggest: { type: Boolean },

            // Internal
            _focusedItemIndex: { type: Number },
            _isOpened: { type: Boolean },
            _inputText: { type: String },

            // a11y / UX helpers
            _isTriggerFocused: { type: Boolean },
            _liveMessage: { type: String },
        };
    }

    constructor() {
        super();

        // Public
        this.identifier = '';
        this.label = '';
        this.placeholder = 'Any';
        this.displayOnlyPlaceholder = false;
        this.items = [];
        this.multiple = true;
        this.selected = [];
        this.autosuggest = false;

        // Internal
        this._focusedItemIndex = -1;
        this._isOpened = false;
        this._inputText = '';

    // a11y / UX helpers
    this._isTriggerFocused = false;
    this._liveMessage = '';

    // timers
    this._liveDebounceTimer = null;
    this._liveClearTimer = null;
    }

    /**
     * Opens or closes the popup when the field is clicked
     */
    fieldClickHandler() {
        // In autosuggest mode, keep click idempotent (open-only).
        // Toggling closed on click causes mobile SR users to type into
        // the collapsed summary text instead of a fresh query.
        if (this.autosuggest) {
            if (!this._isOpened) {
                this.open();
            }
            this.focusInput();
            return;
        }

        this._isOpened ? this.close() : this.open();
    }

    /**
     * Handles keyboard presses in the popup
     * @param  {Object} event
     */
    keyDownHandler(event) {
        // When autosuggest is collapsed, the input may show selected-summary text.
        // If typing starts from this state, clear that display value first so the
        // user types a new query (instead of editing the summary sentence).
        if (
            this.autosuggest &&
            !this._isOpened &&
            this._selectedItemSummaryText &&
            this._isPrintableCharacterKey(event)
        ) {
            this._inputText = '';
            event.target.value = '';
            this.open();
            return;
        }

        switch (event.key) {
            case KEY_ENTER:
            case 13:
                this.handleEnterKey();
                break;
            case KEY_ESCAPE:
            case KEY_ESC:
            case 27:
                this.handleEscapeKey();
                break;
            case KEY_SPACE:
            case KEY_SPACE_BAR:
                // Space opens the listbox when focus is on the combobox trigger
                // (not when typing in the autosuggest input — let browser handle that)
                if (!this.autosuggest) {
                    this._isOpened ? this.handleEnterKey() : this.open();
                    break;
                }
                return;
            case KEY_ARROW_UP:
            case KEY_UP:
            case 38:
                event.altKey
                    ? this.handleAltArrowUpKey()
                    : this.handleArrowUpKey();
                break;
            case KEY_ARROW_DOWN:
            case KEY_DOWN:
            case 40:
                if (!this._isOpened) {
                    this.open();
                }
                event.altKey
                    ? this.handleAltArrowDownKey()
                    : this.handleArrowDownKey();
                break;
            default:
                return;
        }

        event.preventDefault();
    }

    inputChangeHandler(event) {
        const normalizedInputText = this._normalizeAutosuggestInputText(
            event.target.value
        );

        this._inputText = normalizedInputText;
        if (event.target.value !== normalizedInputText) {
            event.target.value = normalizedInputText;
        }
        // Reset focused index when filter text changes so stale highlights are cleared
        this._focusedItemIndex = -1;

        // Expand listbox when the user starts typing (autosuggest only)
        if (this.autosuggest && !this._isOpened) {
            this.open();
        }

        if (this.autosuggest) {
            this._scheduleSuggestionsAnnouncement();
        }
        event.preventDefault();
    }

    isSelectableItem(item) {
        return !!item && item.disabled !== true;
    }

    _isPrintableCharacterKey(event) {
        return (
            !!event &&
            typeof event.key === 'string' &&
            event.key.length === 1 &&
            !event.metaKey &&
            !event.ctrlKey &&
            !event.altKey
        );
    }

    _normalizeAutosuggestInputText(value) {
        if (typeof value !== 'string') {
            return '';
        }

        if (!this.autosuggest) {
            return value;
        }

        // Android/TalkBack can emit input without the expected keydown path.
        // In that case, the field may still contain the collapsed selected-summary
        // text and new characters get appended to it. Strip that prefix so the new
        // value is treated as the user's query.
        const selectedSummary = this._selectedItemSummaryText;
        const hasNoActiveQuery = !this._inputText;
        const startsWithSelectedSummary =
            !!selectedSummary && value.indexOf(selectedSummary) === 0;

        if (!hasNoActiveQuery || !startsWithSelectedSummary) {
            return value;
        }

        return value.substring(selectedSummary.length).replace(/^\s+/, '');
    }

    _scheduleSuggestionsAnnouncement() {
        if (this._liveDebounceTimer) clearTimeout(this._liveDebounceTimer);
        if (this._liveClearTimer) clearTimeout(this._liveClearTimer);

        this._liveDebounceTimer = setTimeout(() => {
            const count = this._visibleItemIndices.length;
            this._liveMessage =
                count === 0
                    ? 'No suggestions available.'
                    : `${count} suggestion${count === 1 ? '' : 's'} available.`;

            this._liveClearTimer = setTimeout(() => {
                this._liveMessage = '';
            }, 1500);
        }, 1000);
    }

    /**
     * Selects an item
     */
    handleEnterKey() {
        if (this._isOpened) {
            const focusedItem =
                this._focusedItemIndex > -1
                    ? this.items[this._focusedItemIndex]
                    : null;
            if (
                this.isSelectableItem(focusedItem) &&
                this.isSuggestedItem(focusedItem.label)
            ) {
                this.selectItem(focusedItem);
            }
        } else {
            this.open();
        }
    }

    /**
     * Try to scoll the active descendent into view in its parent scrolling container
     */
    _scrollFocusedListBoxOptionIntoView() {
        try {
            const el = this.querySelector(`#${this.activeDescendant}`);
            if (!el) {
                return;
            }
            el.scrollIntoView({ block: 'nearest' });
        } catch (error) {}
    }

    /**
     * Returns the indices of items that are currently visible (not hidden by autosuggest filter).
     * @returns {Array<number>}
     */
    get _visibleItemIndices() {
        return this.items.reduce((acc, item, index) => {
            if (this.isSuggestedItem(item.label)) {
                acc.push(index);
            }
            return acc;
        }, []);
    }

    get _navigableItemIndices() {
        return this.items.reduce((acc, item, index) => {
            if (this.isSuggestedItem(item.label) && this.isSelectableItem(item)) {
                acc.push(index);
            }
            return acc;
        }, []);
    }

    /**
     * Navigates down in the popup, skipping hidden (filtered-out) items.
     */
    handleArrowDownKey() {
        const visible = this._navigableItemIndices;
        if (visible.length === 0) return;

        const currentPos = visible.indexOf(this._focusedItemIndex);
        this._focusedItemIndex =
            currentPos < visible.length - 1
                ? visible[currentPos + 1]
                : visible[0];

        this._scrollFocusedListBoxOptionIntoView();
    }

    /**
     * Navigates up in the popup, skipping hidden (filtered-out) items.
     */
    handleArrowUpKey() {
        const visible = this._navigableItemIndices;
        if (visible.length === 0) return;

        const currentPos = visible.indexOf(this._focusedItemIndex);
        this._focusedItemIndex =
            currentPos > 0
                ? visible[currentPos - 1]
                : visible[visible.length - 1];

        this._scrollFocusedListBoxOptionIntoView();
    }

    /**
     * Allows opening the popup with Alt+Down
     */
    handleAltArrowDownKey() {
        this.open();
    }

    /**
     * Allows closing the popup with Alt+Up
     */
    handleAltArrowUpKey() {
        this.close();
    }

    /**
     * Closes the popup on Esc
     */
    handleEscapeKey() {
        this.close(true);
    }

    /**
     * Retrieves a popup item by its value
     * @param  {string} value
     * @returns {Object}
     */
    getItemByValue(value) {
        return this.items.find(
            item => item.value && item.value.toString(10) === value.toString(10)
        );
    }

    /**
     * Handles clicks within the popup
     * @param  {Object} event
     */
    listClickHandler(event) {
        let uiItem = event.target;

        while (uiItem && uiItem.getAttribute('role') !== 'option') {
            uiItem = uiItem.parentNode;
        }
        if (!uiItem) {
            return;
        }
        const itemClicked = this.getItemByValue(uiItem.getAttribute('value'));

        if (!this.isSelectableItem(itemClicked)) {
            return;
        }

        if (itemClicked.selected) {
            this.deselectItem(itemClicked);
        } else {
            this.selectItem(itemClicked);
        }
    }

    /**
     * Handles a Clear click
     * @param  {Object} event
     */
    clearClickHandler(event) {
        event.stopPropagation();
        this.clearSelected();
    }

    clearKeyDownHandler(event) {
        switch (event.key) {
            case KEY_ENTER:
            case KEY_SPACE:
            case KEY_SPACE_BAR:
                event.stopPropagation();
                event.preventDefault();
                this.clearSelected();
                break;
        }
    }

    clearSelected() {
        this.items.forEach(item => {
            item.selected = false;
        });

        this.fireChangeEvent();
    }

    /**
     * Sets an item to selected
     * @param  {Object} item
     */
    selectItem(item) {
        if (!this.multiple) {
            this.items.forEach(item => {
                item.selected = false;
            });
        }

        if (item) {
            if (!this.isSelectableItem(item)) {
                return;
            }
            item.selected = true;
        }

        // Clear typed chars after selection so collapsed field can announce selected values
        if (this.autosuggest) {
            this._inputText = '';
        }

        this.fireChangeEvent();

        if (this.autosuggest) {
            this.focusInput();
        } else {
            this.focusTrigger();
        }

        if (!this.multiple) {
            // Only want to close when this is single select
            this.close();
        }
    }

    /**
     * Deselects an item
     * @param  {Object} item
     */
    deselectItem(item) {
        if (item) {
            item.selected = false;
        }

        this.fireChangeEvent();

        if (this.autosuggest) {
            this.focusInput();
        } else {
            this.focusTrigger();
        }
    }

    /**
     * Lets the parent component know the selection has changed
     */
    fireChangeEvent() {
        const event = new CustomEvent('change', {
            detail: {
                identifier: this.identifier,
                values: this.selectedItemValues(),
            },
            bubbles: true,
        });
        this.dispatchEvent(event);
    }

    /**
     * Opens the popup
     */
    open() {
        this._isOpened = true;
        this._focusedItemIndex = -1;

        if (this.autosuggest) {
            this.focusInput();
        }
    }

    /**
     * Closes the popup
     */
    close(returnFocusToTrigger = false) {
        this._isOpened = false;
        this._focusedItemIndex = -1;

        if (returnFocusToTrigger) {
            this.focusTrigger();
        }
    }

    focusInput() {
        setTimeout(() => {
            this.querySelector('input').focus();
        }, 0);
    }

    focusTrigger() {
        setTimeout(() => {
            // For autosuggest (Breed), the combobox control is the <input>
            const el = this.autosuggest
                ? this.querySelector('input')
                : this.querySelector('.multiselect-field');
            if (el) el.focus();
        }, 0);
    }

    _onTriggerFocus() {
        this._isTriggerFocused = true;
    }

    _onTriggerBlur() {
        this._isTriggerFocused = false;
    }

    get _shouldDescribeAutosuggestInstructions() {
        const hasTyped = !!(this._inputText && this._inputText.trim().length);
        const hasSelections = this.selectedItemLabels().length > 0;
        return !hasTyped && !hasSelections;
    }

    get _autosuggestDisplayValue() {
        const selectedSummary = this._selectedItemSummaryText;

        // When collapsed, expose selected values instead of stale typed chars.
        if (!this._isOpened && selectedSummary) {
            return selectedSummary;
        }

        return this._inputText;
    }

    get _selectedItemSummaryText() {
        const selectedLabels = this.selectedItemLabels();

        if (selectedLabels.length === 0) {
            return '';
        }

        const itemName = this.identifier === 'breed' ? 'breed' : 'option';
        const pluralizedItemName =
            selectedLabels.length === 1 ? itemName : `${itemName}s`;

        return `${selectedLabels.length} ${pluralizedItemName} selected: ${selectedLabels.join(
            ', '
        )}`;
    }

    /**
     * Returns all items marked as selected
     */
    selectedItems() {
        return this.items.filter(item => item.selected === true);
    }

    /**
     * Returns the above as just the labels
     */
    selectedItemLabels() {
        return this.selectedItems().map(item => item.label);
    }

    /**
     * Returns the above as just the values
     */
    selectedItemValues() {
        return this.selectedItems().map(item => item.value);
    }

    /**
     * Returns the index of the search query in the given label
     * @param  {string} label
     */
    autoSuggestSearchIndex(label) {
        return label.toLowerCase().indexOf(this._inputText.toLowerCase());
    }

    /**
     * Determines whether the listbox item should display
     * @param  {string} label
     * @returns {Boolean}
     */
    isSuggestedItem(label) {
        return !this.autosuggest || this.autoSuggestSearchIndex(label) > -1;
    }

    /**
     * If autosuggest is turned on, listbox items should display matched text
     * in bold, otherwise this just returns the given label text
     * @param  {string} label
     * @returns {TemplateResult}
     */
    suggestedItemContent(label) {
        if (!this.autosuggest) {
            return html`
                ${unsafeHTML(label)}
            `;
        }

        const searchIndex = this.autoSuggestSearchIndex(label);
        const indexOfRestOfLabel = searchIndex + this._inputText.length;

        // prettier-ignore
        return html`
            <p>${label.substring(0, searchIndex)}<span class="txt m-txt_heavy">${label.substring(searchIndex, indexOfRestOfLabel)}</span>${label.substring(indexOfRestOfLabel)}</p>
        `;
    }

    /**
     * Hides list items if they are not an autosuggested result.
     * @param  {Object} item
     * @returns {String}
     */
    itemHiddenClass(item) {
        return this.isSuggestedItem(item.label) ? '' : 'u-isVisuallyHidden';
    }

    /**
     * Adds a class to an active list item to indicate focus.
     * @param  {Object} item
     * @returns {String}
     */
    itemFocusedClass(item) {
        return this._focusedItemIndex > -1 &&
            this.items[this._focusedItemIndex].value === item.value
            ? 'multiselect-listItem_focus'
            : '';
    }

    processValue(value) {
        if (value && typeof value === 'string') {
            return value.replace(ID_REGEX, '_');
        }
        return value;
    }

    /**
     * Returns the id of the currently focused list item, or empty string when
     * the listbox is closed (so SRs don't announce a stale descendant).
     * @returns {String}
     */
    get activeDescendant() {
        if (!this._isOpened || this._focusedItemIndex < 0) {
            return '';
        }
        return `${this.identifier}_${this.processValue(
            this.items[this._focusedItemIndex].value
        )}`;
    }

    /**
     * Close listbox when blurred outside
     * @param {Event} event
     */
    blurHandler(event) {
        if (isEventRelatedTargetWithinCurrentTarget(event)) {
            return;
        }

        this.close();
    this._onTriggerBlur();
    }

    /**
     * LitElement life cycle event handler for rendering the markup, using lit-html
     * // WARNING:
     * Visually hiding things so that we we don't have inconsistent blur events focusing the
     * body and triggering close() when elements are display none-ed...
     * @returns {TemplateResult}
     */
    render() {
        const listboxId = `${this.identifier}_listbox`;
        const descriptionId = `${this.identifier}-description`;
        const liveRegionId = `${this.identifier}-live`;
        const popupHidden = !this._isOpened;
        const visibleItemIndices = this._visibleItemIndices;
        const visibleItemPositionByIndex = new Map();
        visibleItemIndices.forEach((itemIndex, visiblePosition) => {
            visibleItemPositionByIndex.set(itemIndex, visiblePosition + 1);
        });
        const visibleItemsCount = visibleItemIndices.length;

        return html`
            <div
                class="multiselectLabel ${this.label
                    ? this.displayOnlyPlaceholder
                        ? 'u-isVisuallyHidden'
                        : ''
                    : 'u-isVisuallyHidden'}"
            >
                <label
                    id="${this.identifier}"
                    class="txt m-txt_md m-txt_bold m-txt_uppercase m-txt_alignCenter"
                >
                    ${this.label}
                </label>
            </div>
            <div class="multiselect ${this._isOpened ? 'u-isOpen' : ''}">
                <div
                    id="${liveRegionId}"
                    class="u-isVisuallyHidden"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    ${this._liveMessage}
                </div>
                ${this.autosuggest
                    ? html`
                          <div
                              id="${descriptionId}"
                              class="u-isVisuallyHidden"
                          >
                              This is a filterable list field. The list of
                              options will be filtered based on your typed
                              query. Use up and down arrow keys or mobile
                              controls to navigate the list. Use enter to
                              select option. Use escape to cancel.
                          </div>

                          <div
                              class="multiselect-search"
                          >
                              <input
                                  type="text"
                                  class="multiselect-search-input"
                                  placeholder="Search By Breed"
                                  @input="${this.inputChangeHandler}"
                                  @keydown="${this.keyDownHandler}"
                                  @click="${this.fieldClickHandler}"
                                  @focus="${this._onTriggerFocus}"
                                  role="combobox"
                                  aria-autocomplete="list"
                                  aria-haspopup="listbox"
                                  aria-controls="${listboxId}"
                                  aria-expanded="${this._isOpened}"
                                  aria-activedescendant="${this.activeDescendant}"
                                  aria-describedby="${this
                                      ._shouldDescribeAutosuggestInstructions
                                      ? descriptionId
                                      : ''}"
                                  aria-labelledby="${this.identifier}"
                                  tabindex="0"
                                  .value="${this._autosuggestDisplayValue}"
                              />
                          </div>
                      `
                    : html`
                          <div
                              class="multiselect-field"
                              tabindex="0"
                              role="combobox"
                              aria-haspopup="listbox"
                              aria-controls="${listboxId}"
                              aria-expanded="${this._isOpened}"
                              aria-activedescendant="${this.activeDescendant}"
                              aria-labelledby="${this.identifier}"
                              @focus="${this._onTriggerFocus}"
                              @click="${this.fieldClickHandler}"
                              @keydown="${this.keyDownHandler}"
                          >
                              <div class="multiselect-field-selection">
                                  ${this.displayOnlyPlaceholder ||
                                  this.selected.length === 0
                                      ? this.placeholder
                                      : this.selectedItemLabels().join(', ')}
                              </div>
                              ${!this.displayOnlyPlaceholder &&
                              this.selected.length > 1
                                  ? html`
                                        <button
                                            class="multiselect-field-clearBtn"
                                            @click="${this.clearClickHandler}"
                                            @keydown="${this
                                                .clearKeyDownHandler}"
                                        >
                                            Clear
                                        </button>
                                    `
                                  : ''}
                          </div>
                      `}
                <div
                    id="${this.identifier}_popup"
                    class="multiselect-popup"
                    ?hidden=${popupHidden}
                    aria-hidden="${popupHidden ? 'true' : 'false'}"
                >
                    <div
                        class="multiselect-popup-list ${this.multiple
                            ? ''
                            : 'multiselect-popup-list_single'}"
                        role="listbox"
                        id="${listboxId}"
                        aria-multiselectable="${this.multiple}"
                        aria-labelledby="${this.identifier}"
                        @click="${this.listClickHandler}"
                    >
                        ${this.items.map(
                            (item, itemIndex) => {
                                const visiblePosition =
                                    visibleItemPositionByIndex.get(itemIndex);
                                const isVisible =
                                    typeof visiblePosition === 'number';

                                return html`
                                    <div
                                        id="${this
                                            .identifier}_${this.processValue(
                                            item.value
                                        )}"
                                        class="multiselect-listItem ${this.itemFocusedClass(
                                            item
                                        )} ${this.itemHiddenClass(item)}"
                                        value="${item.value}"
                                        role="${isVisible
                                            ? 'option'
                                            : 'presentation'}"
                                        aria-hidden="${isVisible
                                            ? 'false'
                                            : 'true'}"
                                        aria-selected="${ifDefined(
                                            isVisible
                                                ? String(
                                                      item.selected === true
                                                  )
                                                : undefined
                                        )}"
                                        aria-disabled="${ifDefined(
                                            isVisible && item.disabled === true
                                                ? 'true'
                                                : undefined
                                        )}"
                                        aria-posinset="${ifDefined(
                                            isVisible
                                                ? String(visiblePosition)
                                                : undefined
                                        )}"
                                        aria-setsize="${ifDefined(
                                            isVisible
                                                ? String(visibleItemsCount)
                                                : undefined
                                        )}"
                                        aria-label="${ifDefined(
                                            item.ariaLabel
                                        )}"
                                        tabindex="-1"
                                    >
                                        <span
                                            class="multiselect-listItem-check"
                                            aria-hidden="true"
                                        >
                                            ${item.selected ? '✓' : ''}
                                        </span>
                                        ${this.suggestedItemContent(item.label)}
                                    </div>
                                `;
                            }
                        )}
                    </div>
                </div>
            </div>
        `;
    }

    /**
     * Use the parent shadow-root
     */
    createRenderRoot() {
        return this;
    }

    /**
     * LitElement life cycle event handler for updated properties
     * @param  {Array} changedProperties
     */
    updated(changedProperties) {
        changedProperties.forEach((oldValue, propName) => {
            if (propName === 'selected') {
                this.items.forEach(item => {
                    item.selected = this.selected.includes(item.value);
                });

                this.requestUpdate();
            }
        });
    }

    firstUpdated(changedProperties) {
        const wrapper = this.querySelector('.multiselect');
        wrapper.addEventListener('focusout', event => this.blurHandler(event));
    }
}

customElements.define('multi-select', MultiSelect);
